import { ChevronDown, ChevronUp, Link as LinkIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge } from '~/components/ui/badge';
import { Card } from '~/components/ui/card';
import type { Feature } from '~/lib/db/schema';
import type { FeatureDefinition } from '~/lib/feature-modules/types';
import { COMPLEXITY_COLORS } from '~/lib/utils';
import DecisionPoint from './DecisionPoint';
import DepthExplorer from './DepthExplorer';
import OptionSelector from './OptionSelector';
import TradeoffPanel from './TradeoffPanel';

interface Props {
  feature: Feature & { definition: FeatureDefinition };
  isRequired?: boolean;
}

export default function FeatureCard({ feature, isRequired }: Props) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-5 py-4 flex items-center gap-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-semibold">{feature.name}</span>
            {isRequired && <Badge variant="secondary">Required</Badge>}
            <span
              className={`text-xs font-medium border rounded-full px-2 py-0.5 ${COMPLEXITY_COLORS[feature.complexity]}`}
            >
              {feature.complexity} complexity
            </span>
          </div>
          <p className="text-sm text-muted-foreground">{feature.description}</p>
        </div>
        <div className="flex-shrink-0 text-muted-foreground">
          {open ? (
            <ChevronUp className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-border px-5 py-4 flex flex-col gap-5">
          {feature.definition.optionGroups.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Options
              </h4>
              <div className="flex flex-col gap-4">
                {feature.definition.optionGroups.map((group) => (
                  <OptionSelector
                    key={group.id}
                    group={group}
                    selected={group.options
                      .filter((o) => o.presets.includes('standard'))
                      .map((o) => o.id)}
                    readOnly
                  />
                ))}
              </div>
            </div>
          )}

          {feature.definition.decisionPoints.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Decision Points
              </h4>
              <div className="flex flex-col gap-2">
                {feature.definition.decisionPoints.map((dp, i) => (
                  <DecisionPoint key={i} point={dp} />
                ))}
              </div>
            </div>
          )}

          {feature.definition.tradeoffs.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Tradeoffs
              </h4>
              <TradeoffPanel tradeoffs={feature.definition.tradeoffs} />
            </div>
          )}

          {feature.definition.depthLayers &&
            feature.definition.depthLayers.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Go Deeper
                </h4>
                <DepthExplorer layers={feature.definition.depthLayers} />
              </div>
            )}

          <div className="pt-1">
            <Link
              to={`/features/${feature.slug}`}
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium"
            >
              <LinkIcon className="size-3.5" />
              View feature detail
            </Link>
          </div>
        </div>
      )}
    </Card>
  );
}
